import { useCallback, useEffect, useRef, useState } from 'react';
import { usePublicClient, useWalletClient } from 'wagmi';
import { useAppKitAccount } from '@reown/appkit/react';
import type { Address, Hash } from 'viem';
import { environment } from '@/config/environment';
import { MONTHLY_DISTRIBUTOR_ABI } from '@/contracts/MonthlyDistributor.abi';
import { ERC20_ABI } from '@/contracts/ERC20.abi';
import { createRpcReader } from '@/utils/rpcRead';

/**
 * Reads and writes for MonthlyDistributor, the recurring monthly payout
 * contract. Entirely separate from useAdmin/useTokenLock, which speak to the
 * vesting factory; the two systems share a token and nothing else.
 */

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

/** Mirrors MonthlyDistributor.MAX_NAME_BYTES. */
export const MAX_ENTRY_NAME_BYTES = 64;
/** Mirrors MonthlyDistributor.MAX_PERIODS. */
export const MAX_PERIODS = 480;
/** Mirrors MonthlyDistributor.MAX_START_LOOKAHEAD, in seconds. */
export const MAX_START_LOOKAHEAD = 730 * 24 * 60 * 60;
/** Mirrors MonthlyDistributor.MIN_START_TIME (2022-01-01T00:00:00Z). */
export const MIN_START_TIME = 1640995200;

/**
 * What the contract measures. Use this in forms, never `name.length`: the two
 * differ by a factor of three for CJK text, so a field that counts characters
 * lets through a label createEntry rejects.
 */
export const entryNameByteLength = (name: string): number =>
  new TextEncoder().encode(name).length;

/**
 * Page sizes the payments list offers, and its default. Mirrors the grants
 * list (useGrantPages): only the visible page is ever read, so the cost of a
 * load does not grow with the roster.
 */
export const PAGE_SIZES = [10, 25, 50] as const;
const DEFAULT_PAGE_SIZE = 25;

/**
 * Entries per pendingInRange call when "Pay everyone due" scans the roster.
 * Measured: 250 entries cost ~3.1M gas, constant whatever the roster size,
 * which sits far below the 50M eth_call cap common on public endpoints.
 */
const SCAN_CHUNK = 250n;

/** Ids per claimMany transaction, so a large run never nears the block gas limit. */
export const CLAIM_BATCH = 100;

/** One row of the roster, as MonthlyDistributor.EntryView decodes. */
export interface DistributorEntry {
  id: bigint;
  name: string;
  recipient: Address;
  totalAmount: bigint;
  paidAmount: bigint;
  /** Date 0. Every later date is one calendar month after the previous one. */
  firstPaymentTime: bigint;
  frontLoaded: boolean;
  periods: number;
  cancelled: boolean;
  payoutCount: bigint;
  payoutsElapsed: bigint;
  /** What the schedule says should have been paid in total by now. */
  cumulativeDue: bigint;
  /** cumulativeDue - paidAmount, clamped. What the next run owes this row. */
  owed: bigint;
  nextPaymentTime: bigint;
  nextPaymentAmount: bigint;
}

export interface DistributorState {
  /** The ERC-20 every entry is denominated and paid in, read from the contract. */
  token: Address;
  owner: Address | null;
  pendingOwner: Address | null;
  claimers: Address[];
  paused: boolean;
  poolBalance: bigint;
  /** Everything owed across the whole roster right now. */
  totalOwed: bigint;
  /** Tokens the pool still needs to settle everything owed right now. */
  fundingRequired: bigint;
  /** Everything the roster will ever still pay out. The solvency figure. */
  totalRemainingObligation: bigint;
  /** The next 26th at 12:00 UTC, from chain time. */
  nextPayoutInstant: bigint;
  /** What the pool needs to settle the roster through nextPayoutInstant. */
  fundingRequiredNextRun: bigint;
  entryCount: bigint;
  /** The connected wallet's balance of `token`, so a funding form can check it. */
  walletBalance: bigint;
  /** How much of `token` the connected wallet has already approved to the pool. */
  allowance: bigint;
  /**
   * ETH held by the contract. It refuses plain transfers, so this is ETH that
   * arrived without asking (selfdestruct, a block reward) and is normally 0.
   */
  ethBalance: bigint;
}

export interface UseDistributorResult {
  /** null when MonthlyDistributor is not deployed on this chain. */
  address: Address | null;
  state: DistributorState | null;
  /** The CURRENT PAGE of the roster -- never the whole of it. */
  entries: DistributorEntry[];
  /** 0-based page shown, and the page count at the current page size. */
  page: number;
  pageCount: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  /** A page read is in flight (the summary has its own `isLoading`). */
  pageLoading: boolean;
  pageError: string | null;
  retryPage: () => void;
  isLoading: boolean;
  error: string | null;
  /** Bumped whenever new data lands; a stable dependency for derived reads. */
  dataVersion: number;
  isOwner: boolean;
  isPendingOwner: boolean;
  isClaimer: boolean;
  /** True for any wallet allowed to trigger a payout for somebody else. */
  canPay: boolean;
  /** The connected wallet, or null. A recipient may always collect their own
   *  entries, so the screen needs this beside `canPay`. */
  account: Address | null;
  refresh: () => Promise<void>;

  createEntry: (params: NewEntryInput) => Promise<void>;
  setRecipient: (id: bigint, recipient: string) => Promise<void>;
  setTotalAmount: (id: bigint, amountWei: bigint) => Promise<void>;
  setName: (id: bigint, name: string) => Promise<void>;
  cancelEntry: (id: bigint) => Promise<void>;
  claimEntries: (ids: bigint[]) => Promise<void>;
  /**
   * Pay everything due across the WHOLE roster, without the roster in memory:
   * scans with pendingInRange, then pays in CLAIM_BATCH-sized transactions.
   * Resolves to how many entries were paid and in how many transactions.
   */
  claimAllDue: () => Promise<{ paid: number; transactions: number }>;
  setClaimer: (account: string, allowed: boolean) => Promise<void>;
  fundPool: (amountWei: bigint) => Promise<void>;
  withdraw: (to: string, amountWei: bigint) => Promise<void>;
  /** Owner-only recovery of ETH forced into the contract. */
  withdrawETH: (to: string, amountWei: bigint) => Promise<void>;
  setPaused: (paused: boolean) => Promise<void>;
  acceptOwnership: () => Promise<void>;
  transferOwnership: (to: string) => Promise<void>;
}

export interface NewEntryInput {
  name: string;
  recipient: string;
  totalAmountWei: bigint;
  /** Unix seconds. Date 0 is the first 26th-at-12:00-UTC at or after it. */
  startTime: number;
  frontLoaded: boolean;
  periods: number;
}

/**
 * The calendar the contract implements, restated for the UI so a form can show
 * the real first payment date before anything is signed. The contract computes
 * its own and is the source of truth; this only has to agree with it, which
 * dapp/test/useDistributor.test.tsx checks against the same cases the Solidity
 * suite uses.
 */
export const PAYOUT_DAY = 26;
export const PAYOUT_HOUR_UTC = 12;

export const payoutInstant = (year: number, month1: number): number =>
  Date.UTC(year, month1 - 1, PAYOUT_DAY, PAYOUT_HOUR_UTC, 0, 0) / 1000;

/** The first 26th-at-12:00-UTC at or after `unixSeconds`. */
export const firstPayoutOnOrAfter = (unixSeconds: number): number => {
  const d = new Date(unixSeconds * 1000);
  const candidate = payoutInstant(d.getUTCFullYear(), d.getUTCMonth() + 1);
  return candidate >= unixSeconds
    ? candidate
    : payoutInstant(d.getUTCFullYear(), d.getUTCMonth() + 2);
};

/** The payout instant `n` calendar months after a payout instant. */
export const payoutAfter = (first: number, n: number): number => {
  const d = new Date(first * 1000);
  return payoutInstant(d.getUTCFullYear(), d.getUTCMonth() + 1 + n);
};

/** Raw EntryView tuples come back from viem as objects; normalise to our shape. */
const toEntry = (raw: any): DistributorEntry => ({
  id: raw.id,
  name: raw.name,
  recipient: raw.recipient,
  totalAmount: raw.totalAmount,
  paidAmount: raw.paidAmount,
  firstPaymentTime: raw.firstPaymentTime,
  frontLoaded: raw.frontLoaded,
  periods: Number(raw.periods),
  cancelled: raw.cancelled,
  payoutCount: raw.payoutCount,
  payoutsElapsed: raw.payoutsElapsed,
  cumulativeDue: raw.cumulativeDue,
  owed: raw.owed,
  nextPaymentTime: raw.nextPaymentTime,
  nextPaymentAmount: raw.nextPaymentAmount,
});

export function useDistributor(
  { selectedId = null }: { selectedId?: bigint | null } = {},
): UseDistributorResult {
  const { address: account } = useAppKitAccount();
  const publicClient = usePublicClient({ chainId: environment.chainId });
  const { data: walletClient } = useWalletClient();

  const distributorAddress = (environment.distributorAddress ?? null) as Address | null;

  const [state, setState] = useState<DistributorState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataVersion, setDataVersion] = useState(0);

  // A load started after an earlier one must win, however they interleave. The
  // token is compared on every setState so a slow first response cannot
  // overwrite a fast second one with stale data.
  const loadToken = useRef(0);

  /**
   * The summary: everything on the screen except the rows themselves.
   *
   * Shaped for a rate-limited public endpoint, the same way the grants reader
   * is (useAdmin.getAllLockDetails):
   *
   *   - Every call goes through ONE createRpcReader, which paces requests
   *     200ms apart and retries a 429 or a timeout. Nothing fans out: the
   *     previous version fired eleven eth_calls at once, and a burst is exactly
   *     what trips a limit.
   *   - Every read is pinned to ONE block, taken first, so the figures agree
   *     with each other even if a claim lands mid-load.
   *   - The cheap fields travel together in one multicall.
   *   - The three HEAVY views -- totalOwed, fundingRequiredThrough and
   *     totalRemainingObligation -- each loop over every entry on chain, at a
   *     measured ~12k gas per entry: ~12M each at 1,000 entries. They go as
   *     SEPARATE requests, because bundled into one multicall they would sum
   *     against a single eth_call's gas cap (commonly 50M) at around 1,000
   *     entries. Separately, each stays under that cap to roughly 4,000.
   *   - fundingRequired is not read at all. It is max(0, totalOwed - pool),
   *     so asking the contract would spend a fourth full-roster loop on a
   *     subtraction.
   *
   * The rows are NOT read here; see the page effect below.
   */
  const load = useCallback(async () => {
    if (!publicClient || !distributorAddress) return;
    const token = ++loadToken.current;
    setIsLoading(true);
    const read = createRpcReader();
    try {
      const blockNumber = await read(() => publicClient.getBlockNumber());
      const base = { address: distributorAddress, abi: MONTHLY_DISTRIBUTOR_ABI } as const;
      const call = (functionName: string, args?: readonly unknown[]) =>
        publicClient.readContract({ ...base, functionName, blockNumber, ...(args ? { args } : {}) } as any);

      const [
        tokenAddress, owner, pendingOwner, claimers, paused, poolBalance, nextPayoutInstantValue, entryCount,
      ] = await read(() => publicClient.multicall({
        allowFailure: false,
        batchSize: 0,
        blockNumber,
        contracts: ['token', 'owner', 'pendingOwner', 'claimers', 'paused', 'poolBalance', 'nextPayoutInstant', 'entryCount']
          .map((functionName) => ({ ...base, functionName })) as any,
      })) as unknown as [Address, Address, Address, Address[], boolean, bigint, bigint, bigint];

      const totalOwed = await read(() => call('totalOwed')) as bigint;
      // Priced at the NEXT payout instant: the number the operator acts on
      // before the 26th, where fundingRequired is what is short today.
      const fundingRequiredNextRun = await read(() =>
        call('fundingRequiredThrough', [nextPayoutInstantValue])) as bigint;
      const totalRemainingObligation = await read(() => call('totalRemainingObligation')) as bigint;

      // The wallet's own position in the token, so the funding form can tell
      // "you do not hold enough" apart from "you have not approved enough".
      // Zero for a disconnected wallet, which is what a read-only view wants.
      const walletToken = { address: tokenAddress, abi: ERC20_ABI } as const;
      const [walletBalance, allowance] = account
        ? await read(() => publicClient.multicall({
            allowFailure: false,
            batchSize: 0,
            blockNumber,
            contracts: [
              { ...walletToken, functionName: 'balanceOf', args: [account as Address] },
              { ...walletToken, functionName: 'allowance', args: [account as Address, distributorAddress] },
            ],
          })) as unknown as [bigint, bigint]
        : [0n, 0n];
      const ethBalance = await read(() => publicClient.getBalance({ address: distributorAddress, blockNumber }));

      if (token !== loadToken.current) return;
      setState({
        token: tokenAddress,
        owner,
        pendingOwner: pendingOwner === ZERO_ADDRESS ? null : pendingOwner,
        claimers,
        paused,
        poolBalance,
        totalOwed,
        fundingRequired: totalOwed > poolBalance ? totalOwed - poolBalance : 0n,
        totalRemainingObligation,
        nextPayoutInstant: nextPayoutInstantValue,
        fundingRequiredNextRun,
        entryCount,
        walletBalance,
        allowance,
        ethBalance,
      });
      setError(null);
      setDataVersion((n) => n + 1);
    } catch (err: any) {
      if (token !== loadToken.current) return;
      setError(err?.shortMessage || err?.message || 'Failed to read the payout contract');
    } finally {
      if (token === loadToken.current) setIsLoading(false);
    }
  }, [publicClient, distributorAddress, account]);

  useEffect(() => {
    void load();
  }, [load]);

  // ------------------------------------------------------------------ pages

  const [requestedPage, setRequestedPage] = useState(0);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [shown, setShown] = useState<{ page: number; rows: DistributorEntry[] }>({ page: -1, rows: [] });
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageAttempt, setPageAttempt] = useState(0);

  const entryCount = state?.entryCount ?? 0n;
  const pageCount = Math.max(1, Math.ceil(Number(entryCount) / pageSize));
  // A deep link to /payroll/entry/:id opens the page that holds it. Ids are
  // roster indices, so that page is arithmetic rather than a search.
  const selectedPage = selectedId !== null && selectedId < entryCount
    ? Number(selectedId / BigInt(pageSize))
    : null;
  const page = selectedPage ?? Math.min(requestedPage, pageCount - 1);
  // Keep the selected entry's page when its details are collapsed.
  useEffect(() => setRequestedPage(page), [page]);

  // Pages cached per data version: a write or a refresh bumps the version and
  // empties the cache, so no page is ever served from before the change.
  const pageCache = useRef<{ version: number; size: number; pages: Map<number, DistributorEntry[]> }>(
    { version: -1, size: 0, pages: new Map() },
  );
  // Serialised, as in useGrantPages: rapid paging queues reads instead of
  // racing them, and a navigated-away read is aborted before it is sent.
  const pageQueue = useRef(Promise.resolve());

  useEffect(() => {
    if (!publicClient || !distributorAddress || !state) return;
    const controller = new AbortController();
    const cache = pageCache.current;
    if (cache.version !== dataVersion || cache.size !== pageSize) {
      pageCache.current = { version: dataVersion, size: pageSize, pages: new Map() };
    }
    const cached = pageCache.current.pages.get(page);
    if (cached) {
      setShown({ page, rows: cached });
      setPageError(null);
      setPageLoading(false);
      return () => controller.abort();
    }
    if (entryCount === 0n) {
      setShown({ page, rows: [] });
      setPageLoading(false);
      return () => controller.abort();
    }
    // Moving to another page clears the list rather than showing the previous
    // page's rows under the new page's number. A refresh of the SAME page keeps
    // its rows up until the new ones land, so a write does not blank the list.
    setShown((current) => (current.page === page ? current : { page, rows: [] }));
    setPageError(null);
    setPageLoading(true);

    const version = dataVersion;
    const start = BigInt(page) * BigInt(pageSize);
    const end = start + BigInt(pageSize) > entryCount ? entryCount : start + BigInt(pageSize);
    pageQueue.current = pageQueue.current.then(async () => {
      if (controller.signal.aborted) return;
      try {
        // One getEntries call is one page, whatever its size: measured at a
        // constant ~0.67M gas for 25 rows at any roster size.
        const read = createRpcReader(controller.signal);
        const raw = await read(() => publicClient.readContract({
          address: distributorAddress,
          abi: MONTHLY_DISTRIBUTOR_ABI,
          functionName: 'getEntries',
          args: [start, end],
        })) as any[];
        if (controller.signal.aborted) return;
        const rows = raw.map(toEntry);
        if (pageCache.current.version === version) pageCache.current.pages.set(page, rows);
        setShown({ page, rows });
      } catch {
        if (!controller.signal.aborted) setPageError('This page of payments could not be loaded. Please retry.');
      } finally {
        if (!controller.signal.aborted) setPageLoading(false);
      }
    });
    return () => controller.abort();
    // `state` is deliberately absent: dataVersion changes exactly when a new
    // state lands, and state's identity would re-run this on unrelated renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicClient, distributorAddress, dataVersion, page, pageSize, entryCount, pageAttempt]);

  const setPage = useCallback((next: number) => setRequestedPage(Math.max(0, next)), []);
  const setPageSize = useCallback((size: number) => {
    setRequestedPage(0);
    setPageSizeState(size);
  }, []);
  const entries = shown.page === page ? shown.rows : [];

  // A mined receipt can still represent a revert. Every write below goes through
  // this before the UI reloads or reports success.
  const waitForSuccessfulReceipt = async (hash: Hash) => {
    if (!publicClient) throw new Error('No RPC connection');
    const receipt = await publicClient.waitForTransactionReceipt({ hash });
    if (receipt.status === 'reverted') {
      throw new Error('The transaction reverted on-chain. No changes were made.');
    }
    return receipt;
  };

  /**
   * Wait for the endpoint serving reads to catch up to `blockNumber` before
   * reloading.
   *
   * Public RPC endpoints are load balanced, and a read issued straight after a
   * transaction routinely lands on a replica that has not seen it yet. That is
   * measured, not theoretical: on Base Sepolia a `totalOwed()` read moments
   * after a payout came back at its full pre-payment value. Without this, the
   * reload after every write here would show the roster exactly as it was, and
   * the operator would conclude the transaction had not worked and send it
   * again.
   *
   * Bounded, and it never throws: if the endpoint is still behind after the
   * budget, reloading stale data is a cosmetic problem the Refresh button
   * fixes, whereas failing the write here would report a transaction that
   * actually succeeded as a failure. That is the worse of the two.
   */
  const waitForBlock = async (blockNumber: bigint, budgetMs = 15_000) => {
    if (!publicClient) return;
    const deadline = Date.now() + budgetMs;
    for (;;) {
      try {
        if ((await publicClient.getBlockNumber()) >= blockNumber) return;
      } catch {
        // A failed head read is not worth surfacing; the deadline still applies.
      }
      if (Date.now() >= deadline) return;
      await new Promise((resolve) => setTimeout(resolve, 1_000));
    }
  };

  const write = useCallback(
    async (functionName: string, args: readonly unknown[]) => {
      if (!walletClient || !publicClient) throw new Error('Wallet not connected');
      if (!distributorAddress) throw new Error('The payout contract is not deployed on this chain');
      setIsLoading(true);
      try {
        const hash = await walletClient.writeContract({
          address: distributorAddress,
          abi: MONTHLY_DISTRIBUTOR_ABI,
          functionName: functionName as any,
          args: args as any,
        });
        const receipt = await waitForSuccessfulReceipt(hash);
        await waitForBlock(receipt.blockNumber);
      } finally {
        setIsLoading(false);
      }
      await load();
    },
    [walletClient, publicClient, distributorAddress, load],
  );

  const lower = account?.toLowerCase() ?? null;
  const isOwner = !!lower && state?.owner?.toLowerCase() === lower;
  const isPendingOwner = !!lower && state?.pendingOwner?.toLowerCase() === lower;
  const isClaimer = !!lower && !!state?.claimers.some((c) => c.toLowerCase() === lower);

  const createEntry = useCallback(async (params: NewEntryInput) => {
    const bytes = entryNameByteLength(params.name.trim());
    if (bytes === 0) {
      throw new Error('A name is required — it is what identifies this payment.');
    }
    if (bytes > MAX_ENTRY_NAME_BYTES) {
      throw new Error(
        `Name is ${bytes} bytes of UTF-8; the limit is ${MAX_ENTRY_NAME_BYTES}. ` +
        'The contract counts bytes, not characters.',
      );
    }
    if (params.periods < 1 || params.periods > MAX_PERIODS) {
      throw new Error(`Periods must be between 1 and ${MAX_PERIODS}.`);
    }
    if (params.totalAmountWei <= 0n) throw new Error('The total must be above zero.');
    await write('createEntry', [{
      name: params.name.trim(),
      recipient: params.recipient as Address,
      totalAmount: params.totalAmountWei,
      startTime: BigInt(params.startTime),
      frontLoaded: params.frontLoaded,
      // `periods` is a uint32, so it stays a plain number — a bigint would not
      // encode. Every other numeric argument on this call is a bigint.
      periods: params.periods,
    }]);
  }, [write]);

  /**
   * Top the pool up, approving first when the existing allowance is short.
   *
   * Two transactions in that case, and deliberately not an infinite approval:
   * the pool is funded roughly once a month for an amount that is known exactly
   * beforehand, so an unlimited allowance would buy one saved signature a month
   * in exchange for a standing claim on the treasury wallet's whole balance.
   *
   * An existing non-zero allowance that is merely too small is raised straight
   * to the amount needed. The reset-to-zero dance some older tokens require is
   * not done here because this contract is pointed at one known token which
   * does not need it; see PAYROLL.md if that ever changes.
   */
  const fundPool = useCallback(async (amountWei: bigint) => {
    if (!walletClient || !publicClient) throw new Error('Wallet not connected');
    if (!distributorAddress) throw new Error('The payout contract is not deployed on this chain');
    if (amountWei <= 0n) throw new Error('Enter an amount above zero.');
    if (!state) throw new Error('The payout contract has not been read yet.');
    if (amountWei > state.walletBalance) {
      throw new Error('That is more than this wallet holds.');
    }
    setIsLoading(true);
    try {
      if (state.allowance < amountWei) {
        const approval = await walletClient.writeContract({
          address: state.token,
          abi: ERC20_ABI,
          functionName: 'approve',
          args: [distributorAddress, amountWei],
        });
        await waitForSuccessfulReceipt(approval);
      }
      const hash = await walletClient.writeContract({
        address: distributorAddress,
        abi: MONTHLY_DISTRIBUTOR_ABI,
        functionName: 'fund',
        args: [amountWei],
      });
      const receipt = await waitForSuccessfulReceipt(hash);
      await waitForBlock(receipt.blockNumber);
    } finally {
      setIsLoading(false);
    }
    await load();
  }, [walletClient, publicClient, distributorAddress, state, load]);

  /** Sweep tokens out of the pool. The contract takes the token address so that
   *  anything sent here by mistake is recoverable too; this only ever passes the
   *  payout token, which is the case the screen offers. */
  const withdraw = useCallback(async (to: string, amountWei: bigint) => {
    if (!state) throw new Error('The payout contract has not been read yet.');
    if (amountWei <= 0n) throw new Error('Enter an amount above zero.');
    await write('withdraw', [state.token, to as Address, amountWei]);
  }, [write, state]);

  const claimAllDue = useCallback(async () => {
    if (!walletClient || !publicClient) throw new Error('Wallet not connected');
    if (!distributorAddress) throw new Error('The payout contract is not deployed on this chain');
    setIsLoading(true);
    try {
      // Find who is owed without holding the roster: pendingInRange returns only
      // the entries that owe something, in bounded chunks, all at one block.
      const read = createRpcReader();
      const blockNumber = await read(() => publicClient.getBlockNumber());
      const count = await read(() => publicClient.readContract({
        address: distributorAddress, abi: MONTHLY_DISTRIBUTOR_ABI, functionName: 'entryCount', blockNumber,
      })) as bigint;
      const ids: bigint[] = [];
      for (let start = 0n; start < count; start += SCAN_CHUNK) {
        const end = start + SCAN_CHUNK > count ? count : start + SCAN_CHUNK;
        const [pageIds] = await read(() => publicClient.readContract({
          address: distributorAddress, abi: MONTHLY_DISTRIBUTOR_ABI, functionName: 'pendingInRange',
          args: [start, end], blockNumber,
        })) as unknown as [readonly bigint[], readonly bigint[], bigint];
        ids.push(...pageIds);
      }
      if (ids.length === 0) throw new Error('Nothing is due.');

      let paid = 0;
      let transactions = 0;
      let lastBlock: bigint | null = null;
      for (let i = 0; i < ids.length; i += CLAIM_BATCH) {
        const batch = ids.slice(i, i + CLAIM_BATCH);
        let hash: Hash;
        try {
          hash = await walletClient.writeContract({
            address: distributorAddress,
            abi: MONTHLY_DISTRIBUTOR_ABI,
            functionName: 'claimMany',
            args: [batch],
          });
        } catch (err: any) {
          // An earlier batch drained the pool, so this one would pay nobody and
          // claimMany refuses it. Stop and report what WAS paid -- the same
          // handling as scripts/runPayout.ts. Anything else is a real failure.
          if (transactions > 0 && /NothingDue/.test(String(err?.message ?? ''))) break;
          throw err;
        }
        const receipt = await waitForSuccessfulReceipt(hash);
        lastBlock = receipt.blockNumber;
        paid += batch.length;
        transactions += 1;
      }
      if (lastBlock !== null) await waitForBlock(lastBlock);
      return { paid, transactions };
    } finally {
      setIsLoading(false);
      await load();
    }
  }, [walletClient, publicClient, distributorAddress, load]);

  const claimEntries = useCallback(async (ids: bigint[]) => {
    if (ids.length === 0) throw new Error('Nothing to pay.');
    // claimMany over an explicit list rather than claimRange: the roster is
    // filtered client-side to the rows that actually owe something, so a run
    // never spends gas walking rows the contract would skip.
    await write('claimMany', [ids]);
  }, [write]);

  return {
    address: distributorAddress,
    state,
    entries,
    page,
    pageCount,
    pageSize,
    setPage,
    setPageSize,
    pageLoading,
    pageError,
    retryPage: () => setPageAttempt((n) => n + 1),
    isLoading,
    error,
    dataVersion,
    isOwner,
    isPendingOwner,
    isClaimer,
    canPay: isOwner || isClaimer,
    account: (account as Address | undefined) ?? null,
    refresh: load,
    createEntry,
    claimEntries,
    claimAllDue,
    setRecipient: useCallback((id, recipient) =>
      write('setRecipient', [id, recipient as Address]), [write]),
    setTotalAmount: useCallback((id, amountWei) =>
      write('setTotalAmount', [id, amountWei]), [write]),
    setName: useCallback((id, name) => {
      const bytes = entryNameByteLength(name.trim());
      if (bytes === 0) throw new Error('A name is required.');
      if (bytes > MAX_ENTRY_NAME_BYTES) {
        throw new Error(`Name is ${bytes} bytes of UTF-8; the limit is ${MAX_ENTRY_NAME_BYTES}.`);
      }
      return write('setName', [id, name.trim()]);
    }, [write]),
    cancelEntry: useCallback((id) => write('cancel', [id]), [write]),
    setClaimer: useCallback((accountAddress, allowed) =>
      write('setClaimer', [accountAddress as Address, allowed]), [write]),
    withdraw,
    withdrawETH: useCallback(async (to: string, amountWei: bigint) => {
      if (amountWei <= 0n) throw new Error('Enter an amount above zero.');
      await write('withdrawETH', [to as Address, amountWei]);
    }, [write]),
    fundPool,
    setPaused: useCallback((paused) => write(paused ? 'pause' : 'unpause', []), [write]),
    acceptOwnership: useCallback(() => write('acceptOwnership', []), [write]),
    transferOwnership: useCallback((to) =>
      write('transferOwnership', [to as Address]), [write]),
  } as UseDistributorResult;
}
